import { useCallback, useEffect, useState } from 'react';
import { useRootStore } from '@nandosaus/state-management';
import moment from 'moment';

import { ShortLeadTimeErrorModal } from '../../components/order-settings-modal-catering/short-lead-time-error-modal';

export const useCateringShortLeadModal = ({
  enabled = true,
  onDismiss,
  pickupDate: pickupDateOverride,
  pickupTime: pickupTimeOverride,
} = {}) => {
  const { CartStore, OrderingContextStore } = useRootStore();

  const { restaurant } = CartStore;
  const subtotalCents = CartStore.subtotalPrices?.cents || 0;
  // Allow callers (e.g. the in-modal picker) to detect against in-flight edits
  // rather than the committed store value.
  const cateringPickupDate =
    pickupDateOverride !== undefined ? pickupDateOverride : OrderingContextStore.cateringPickupDate;
  const cateringPickupTime =
    pickupTimeOverride !== undefined ? pickupTimeOverride : OrderingContextStore.cateringPickupTime;

  const [shortLeadModal, setShortLeadModal] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [cateringPickupDate, subtotalCents]);

  useEffect(() => {
    if (!enabled || shortLeadModal || dismissed) return;
    if (!restaurant?.cateringShortLeadTimes || !cateringPickupDate) return;

    const today = moment().startOf('day');
    const pickup = moment(cateringPickupDate).startOf('day');
    const daysFromToday = pickup.diff(today, 'days');

    if (daysFromToday === 0 && restaurant.isSameDayCateringPastCutoff(pickup.toDate())) {
      const entry = restaurant.cateringWindow.find(e => e.dayIndex === today.day());
      setShortLeadModal({
        variant: ShortLeadTimeErrorModal.VARIANTS.PAST_CUTOFF,
        cutoffTime: entry?.cateringPickupWindowSameDayCutOffTime || '',
        requestedTime: cateringPickupTime ? moment(cateringPickupTime).format('h:mm A') : '',
      });
      return;
    }

    if (daysFromToday >= 0 && daysFromToday <= 1) {
      const maxValue = restaurant.effectiveCateringMaxOrderValue;
      const subtotalDollars = subtotalCents / 100;
      if (maxValue != null && subtotalDollars >= maxValue) {
        setShortLeadModal({
          variant: ShortLeadTimeErrorModal.VARIANTS.OVER_CAP,
          maxValue,
          standardLeadHours: restaurant.cateringStandardLeadTimeHoursForToday,
        });
      }
    }
  }, [enabled, shortLeadModal, dismissed, cateringPickupDate, cateringPickupTime, restaurant, subtotalCents]);

  const dismissShortLeadModal = useCallback(() => {
    const current = shortLeadModal;
    setShortLeadModal(null);
    setDismissed(true);
    if (onDismiss) onDismiss(current);
  }, [shortLeadModal, onDismiss]);

  return { shortLeadModal, dismissShortLeadModal };
};
